import styled, { createGlobalStyle } from "styled-components";
import { publicUrl } from "@/config";
import { themes } from "./ThemeContext";

const FOOTPRINT_SVG_URL = `${publicUrl}/images/footprint.svg`;

/** Breakpoints para responsive (5 resoluciones) */
const BP_MOBILE = 639;       // Móvil: 0-639px
const BP_TABLET_MIN = 640;   // Tablet: 640-1023px
const BP_TABLET = 1023;
const BP_SMALL_PC_MIN = 1024; // PC pequeño: 1024-1279px
const BP_SMALL_PC = 1279;
const BP_MEDIUM_PC = 1280;   // PC mediano: 1280-1599px
const BP_LARGE_PC = 1600;    // PC grande: 1600px+

export const FooterLayoutStyle = createGlobalStyle`
    #root .ms-Grid:first-of-type
    {
        padding-bottom: 0;
    }

    /* Grandes resoluciones (1280px+): footer al final cuando el contenido es corto */
    @media (min-width: 1280px)
    {
        #root .ms-Grid:first-of-type
        {
            display: flex;
            flex-direction: column;
            min-height: 100vh;
        }
        #root .ms-Grid:first-of-type > .ms-Grid-row
        {
            flex: 1;
        }
    }
`;

const getFooterTheme = (themeName: string) =>
{
    return themeName === "dark" ? themes.dark : themes.light;
};

const FOOTER_LEFT_PADDING_PX = 40;

export const StyledFooter = styled.footer<{themeName?: string}>`
    position: relative;
    width: 100%;
    min-height: 400px;
    padding: 0 16px 2em 16px;
    overflow-x: hidden;

    @media (min-width: ${BP_MEDIUM_PC}px)
    {
        margin-left: -${FOOTER_LEFT_PADDING_PX}px;
        padding-left: ${FOOTER_LEFT_PADDING_PX}px;
    }
    box-shadow: ${props =>
    {
        const theme = getFooterTheme(props.themeName ?? "light");
        return theme.shadow;
    }};
    color: ${props =>
    {
        const theme = getFooterTheme(props.themeName ?? "light");
        return theme.name === "dark" ? theme.neutralPrimary : "#FFFFFF";
    }};
    background: ${props =>
    {
        const theme = getFooterTheme(props.themeName ?? "light");
        return theme.name === "dark" ? theme.darkBackground : theme.darkBackground;
    }};
    button
    {
        background: transparent;
        border: 0;
    }
    .footprint
    {
        width: 100%;
        max-width: 374px;
        aspect-ratio: 374 / 470;
        box-sizing: border-box;
        position: relative;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 2em 1.5em;
        text-align: center;

        &:before
        {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 0;
            background-image: url("${FOOTPRINT_SVG_URL}");
            background-size: cover;
            background-position: center bottom;
            background-repeat: no-repeat;
        }

        .home-link
        {
            position: relative;
            z-index: 1;
            height: fit-content;
            padding: 0;
            margin: 0;
            cursor: pointer;

            &:hover,
            &:focus,
            &:active
            {
                background: transparent !important;
            }

            img
            {
                height: clamp(80px, 15vw, 114px);
                width: auto;
                margin-bottom: clamp(-20px, -5vw, -27px);
                display: block;
            }
        }

        .slogan,
        .separator,
        .copyright
        {
            position: relative;
            z-index: 1;
            margin: 0;
        }

        .slogan
        {
            margin-top: 0.25em;
        }

        .separator
        {
            align-self: stretch;
            margin: 0.5em 0;
        }

        .copyright
        {
            margin-top: 0;
        }
    }
    .ms-Grid-row.footer-container
    {
        display: flex;
        flex-wrap: wrap;
        align-items: stretch;

        > .ms-Grid-col
        {
            &:first-child
            {
                display: flex;
                justify-content: center;
                align-items: center;
                padding-top: 1em;
            }

            &:nth-child(n+2)
            {
                padding-top: 76px;
            }

            &:last-child
            {
                text-wrap: balance;
                padding-right: 20px;
                font-size: 14px;
                line-height: 20px;
            }
        }
    }
    .slogan
    {
        font-size: 24px;
        font-weight: 200;
        line-height: 32px;
        opacity: 0.7;
    }
    .separator
    {
        opacity: 0.2;
        margin-top: 12px;
        margin-bottom: 13px;
    }
    .copyright
    {
        font-size: 16px;
        font-weight: 100;
        line-height: 22px;
        opacity: 0.4;
    }
    .title
    {
        font-size: 14px;
        line-height: 20px;
        opacity: 0.5;
        margin-top: 30px;
        margin-bottom: 24px;
        display: block;
        cursor: default;
        color: #31AB8D;
    }
    .titleSmall
    {
        font-size: 14px;
        line-height: 20px;
        opacity: 0.5;
        margin-top: 30px;
        margin-bottom: 8px;
        display: block;
    }
    .titleSede
    {
        font-size: 14px;
        line-height: 20px;
        margin-top: 0;
        margin-bottom: 24px;
        display: block;
        max-width: 600px;
        text-wrap: balance;
        cursor: default;
        a
        {
            color: ${props =>
            {
                const theme = getFooterTheme(props.themeName ?? "light");
                return theme.themePrimary;
            }};
        }
    }
    .links
    {
        font-size: 14px;
        line-height: 20px;
        display: inline-block;
        a
        {
            color: inherit;
            text-decoration: none;
            &:hover
            {
                color: #31AB8D;
                cursor: pointer;
            },
            &:visited
            {
                color: inherit;
            }
        }
        > *
        {
            margin-left: 72px;
            &:first-of-type
            {
                margin-left: 0;
            }
        }
    }
    .social-networks
    {
        display: inline-flex;
        a
        {
            color: inherit !important;
            background: ${props =>
            {
                const theme = getFooterTheme(props.themeName ?? "light");
                return theme.quinary;
            }};
            &:hover,
            &:visited
            {
                color: inherit !important;
            }
        }
    }
    .sponsors
    {
        display: inline-block;
        margin-top: -15px;
        .ms-Image
        {
            vertical-align: bottom;
            margin-left: 92px;
            display: inline-block;
            &:first-child
            {
                margin-left: 0;
            }
        }
    }
    .related-entities
    {
        display: flex;
        flex-direction: column;
    }

    .related-entities-row
    {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        gap: 32px 48px;
        width: 100%;
    }

    .related-entities-col-diaphanum
    {
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;

        a
        {
            display: flex;
            align-items: center;
        }

        .ms-Image,
        img
        {
            max-width: 100%;
            height: auto;
            vertical-align: middle;
        }
    }

    .related-entities-col-fintech
    {
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
        padding-top: 0;

        a
        {
            display: flex;
            align-items: center;
        }

        .ms-Image,
        img
        {
            max-width: 100%;
            height: auto;
            vertical-align: middle;
        }
    }

    .related-entities-col-fintech-mobile
    {
        display: none;
    }

    .related-entities-col-fintech-desktop
    {
        display: flex;
    }

    @media (max-width: 1023px)
    {
        .related-entities-col-fintech-mobile
        {
            display: flex;
        }

        .related-entities-col-fintech-desktop
        {
            display: none;
        }
    }

    @media (max-width: ${BP_MOBILE}px)
    {
        .related-entities-row
        {
            flex-direction: column;
            align-items: center;
            justify-content: flex-start;
        }

        .related-entities-col-diaphanum,
        .related-entities-col-fintech
        {
            flex: 0 0 auto;
            justify-content: center;
        }

        .related-entities-col-fintech
        {
            padding-top: 2em;
        }
    }

    .client-warning
    {
        font-size: 14px;
        line-height: 20px;
        margin: 0 0 24px 0;
        opacity: 0.9;
        word-wrap: break-word;
        overflow-wrap: break-word;
    }

    /* Tablet: 640-1023px - 2 columnas 50% (footprint + links), footprint con arco, sin simplificar */
    @media (min-width: ${BP_TABLET_MIN}px) and (max-width: ${BP_TABLET}px)
    {
        margin-left: -16px;
        padding-left: 16px;
        .ms-Grid-row.footer-container
        {
            display: flex;
            flex-wrap: wrap;
        }
        .ms-Grid-row.footer-container > .ms-Grid-col:first-child,
        .ms-Grid-row.footer-container > .ms-Grid-col:nth-child(2)
        {
            flex: 0 0 50%;
            max-width: 50%;
            box-sizing: border-box;
        }
        .ms-Grid-row.footer-container > .ms-Grid-col:last-child
        {
            flex: 0 0 100%;
            max-width: 100%;
            padding-top: 48px;
        }
        .ms-Grid-row.footer-container > .ms-Grid-col:last-child > p
        {
            text-align: center;
        }
        .ms-Grid-row.footer-container > .ms-Grid-col:first-child
        {
            display: flex;
            justify-content: center;
            align-items: center;
            padding-top: 1em;
        }
        .ms-Grid-row.footer-container > .ms-Grid-col:nth-child(2)
        {
            padding-top: 76px;
        }
        .footprint
        {
            max-width: 300px;
        }
    }

    /* 640-952px: links de interés en 2 filas bien centradas (evita superposición) */
    @media (min-width: 640px) and (max-width: 952px)
    {
        .ms-Grid-row.footer-container > .ms-Grid-col:nth-child(2) .links
        {
            display: grid;
            grid-template-columns: repeat(2, auto);
            justify-content: center;
            justify-items: center;
            gap: 0.75em 2em;
        }
        .ms-Grid-row.footer-container > .ms-Grid-col:nth-child(2) .links > *
        {
            margin-left: 0 !important;
        }
    }

    /* PC pequeño: 1024-1279px (columnas en 3, sin margin-left) */
    @media (min-width: ${BP_SMALL_PC_MIN}px) and (max-width: ${BP_SMALL_PC}px)
    {
        margin-left: -${FOOTER_LEFT_PADDING_PX}px;
        padding-left: ${FOOTER_LEFT_PADDING_PX}px;
        .ms-Grid-row.footer-container
        {
            display: flex;
            flex-wrap: nowrap;
        }
        .ms-Grid-row.footer-container > .ms-Grid-col
        {
            flex: 0 0 33.333%;
            max-width: 33.333%;
            box-sizing: border-box;
        }
        .ms-Grid-row.footer-container > .ms-Grid-col:nth-child(n+2)
        {
            padding-top: 76px;
            padding-left: 1em;
        }
        .ms-Grid-row.footer-container > .ms-Grid-col:first-child
        {
            display: flex;
            justify-content: center;
            align-items: center;
            padding-top: 1em;
        }
        .ms-Grid-row.footer-container > .ms-Grid-col:nth-child(2) .links
        {
            display: grid;
            grid-template-columns: repeat(2, auto);
            justify-content: center;
            justify-items: center;
            gap: 0.75em 2em;
        }
        .ms-Grid-row.footer-container > .ms-Grid-col:nth-child(2) .links > *
        {
            margin-left: 0 !important;
        }
    }

    /* PC grande: 1600px+ */
    @media (min-width: ${BP_LARGE_PC}px)
    {
        .footprint
        {
            max-width: 400px;
        }
        .ms-Grid-row.footer-container > .ms-Grid-col:nth-child(n+2)
        {
            padding-top: 84px;
        }
    }

    /* Móvil: 0 - 639px */
    @media (max-width: ${BP_MOBILE}px)
    {
        min-height: auto;
        .ms-Grid-row.footer-container > .ms-Grid-col
        {
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            padding-left: 32px;
            padding-right: 32px;
            box-sizing: border-box;
        }
        .ms-Grid-row.footer-container > .ms-Grid-col:first-child
        {
            padding-top: 1em;
        }
        .ms-Grid-row.footer-container > .ms-Grid-col:nth-child(2),
        .ms-Grid-row.footer-container > .ms-Grid-col:last-child
        {
            padding-top: 2em;
        }
        .footprint
        {
            width: 100%;
            max-width: 100%;
            min-height: auto;
            aspect-ratio: auto;
            margin: 0;
            padding: 2em 0;
            background: transparent;
            overflow: visible;
            gap: 1.5em;

            &:before
            {
                display: none;
            }

            .home-link
            {
                img
                {
                    height: clamp(48px, 14vw, 68px);
                    width: auto;
                    margin-bottom: 0;
                }
            }
            .slogan
            {
                margin-top: 0;
                font-size: 22px;
                line-height: 30px;
                opacity: 0.75;
            }
            .separator
            {
                align-self: center;
                width: 60%;
                max-width: 180px;
                margin: 0;
                opacity: 0.3;
            }
            .copyright
            {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 0.15em;
                font-size: 14px;
                line-height: 20px;
                opacity: 0.5;

                .copyright-line2
                {
                    display: block;
                }
            }
        }
        .title,
        .titleSede
        {
            font-size: 16px;
            line-height: 22px;
        }
        .ms-Grid-row.footer-container .ms-Grid-col:last-child > p
        {
            font-size: 16px;
            line-height: 22px;
        }
        .ms-Grid-row.footer-container > .ms-Grid-col:last-child
        {
            font-size: 16px;
            line-height: 22px;
        }
        .ms-Grid-row.footer-container > .ms-Grid-col:nth-child(2) .links
        {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 0 24px;
            font-size: 16px;
            line-height: 22px;

            > *
            {
                margin-left: 0 !important;
            }
        }
        .ms-Grid-row.footer-container > .ms-Grid-col:nth-child(2) .social-networks
        {
            justify-content: center;
        }
        .ms-Grid-row.footer-container .ms-Grid-col:last-child
        {
            font-size: 16px;
            line-height: 22px;

            .title:first-child
            {
                margin-top: 20px;
            }
        }
        .sponsors
        {
            .ms-Image
            {
                padding: 15px 0;
                display: block;
                margin: 0;
            }
        }
    }
`;

export const StyledFintechColMobile = styled.div`
    padding-top: 2em;
`;

export const StyledFintechColDesktop = styled.div`
    padding-top: 0;
`;

export const StyledClientWarning = styled.p`
    font-size: 14px;
    line-height: 20px;
    margin: 0 0 24px 0;
    opacity: 0.9;
    word-wrap: break-word;
    overflow-wrap: break-word;
    cursor: default;
`;